import { FilterList } from "@mui/icons-material";
import { Badge, Box, Checkbox, Chip, Pagination, Tooltip } from "@mui/material";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import AccessDenied from "components/AccessDenied";
import AlertDialog from "components/AlertDialog";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import TableSearch from "components/TableSearch";
import { useLanguage } from "contexts/language.context";
import moment from "moment";
import { useEffect, useMemo, useState, useRef } from "react";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import DataTable from "examples/Tables/DataTable";
import ncrService from "services/ncr-service";
import useOpen from "utils/useOpen";
import FiltersDialog from "layouts/tasks/components/FiltersDialog";
import GenerateNcrDialog from "./GenerateNcrDialog";
import NcrEditDialog from "./NcrEditDialog";

const NCR_STATUS_COLORS = {
  1: "warning",
  2: "success",
};

export default function AllNcr({ permissions }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [ncrs, setNcrs] = useState(null);
  const [count, setCount] = useState(0);
  const [query, setQuery] = useState({});
  const [options, setOptions] = useState({
    statuses: null,
    users: null,
    branches: null,
    branchGroups: null,
    audits: null,
  });
  const [searchSignal, setSearchSignal] = useState(false);
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [ncrToDelete, setNcrToDelete] = useState(null);
  const [selectingNcrsToPrint, setSelectingNcrsToPrint] = useState(false);
  const [selectedNcrIds, setSelectedNcrIds] = useState([]);
  const [printingMultiple, setPrintingMultiple] = useState(false);
  const open = useOpen({
    filters: false,
    generate: false,
    edit: false,
  });
  const { label, languageId } = useLanguage();
  const listRequest = useRef(0);
  const generationDirty = useRef(false);
  const filtersLoaded = useRef(false);
  const latestListResult = useRef({ data: [], count: 0 });
  const searchHandler = useRef(null);

  const columns = [
    {
      Header: label("code-singular") || "Code",
      accessor: "code",
    },
    {
      Header: label("branch-group-singular"),
      accessor: "branchGroup",
    },
    {
      Header: label("status-singular"),
      accessor: "status",
    },
    {
      Header: label("reference-clause-singular") || "Reference clause",
      accessor: "referenceClause",
    },
    {
      Header: label("person-in-charge-singular") || "Person in charge",
      accessor: "personInCharge",
    },
    {
      Header: label("estimated-closure-date-singular") || "Estimated closure",
      accessor: "estimatedClosureDate",
    },
    {
      Header: label("date-singular"),
      accessor: "creationDate",
    },
  ];

  if (selectingNcrsToPrint && (permissions.get || permissions.getMy)) {
    columns.unshift({
      Header: (
        <Checkbox
          size="small"
          aria-label={label("select-all-ncrs-on-page") || "Select all NCRs on this page"}
          checked={Boolean(ncrs?.length) && ncrs.every((ncr) => selectedNcrIds.includes(ncr.id))}
          indeterminate={ncrs?.some((ncr) => selectedNcrIds.includes(ncr.id)) && !ncrs.every((ncr) => selectedNcrIds.includes(ncr.id))}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => {
            const pageIds = ncrs?.map((ncr) => ncr.id) || [];
            setSelectedNcrIds((current) => {
              if (!event.target.checked) return current.filter((id) => !pageIds.includes(id));
              const additions = pageIds.filter((id) => !current.includes(id));
              return [...current, ...additions.slice(0, Math.max(0, 50 - current.length))];
            });
          }}
        />
      ),
      accessor: "printSelection",
      width: "48px",
      align: "center",
    });
  }

  if (permissions.update || permissions.get || permissions.getMy || permissions.create || permissions.delete) {
    columns.push({
      Header: "",
      accessor: "actions",
      align: "right",
    });
  }

  async function getOptions() {
    const statusesResponse = await ncrService.getNcrStatuses();

    if (statusesResponse[0].ok) {
      setOptions((options) => ({
        ...options,
        statuses: statusesResponse[1].map((item) => ({
          id: item.id,
          label: item.name,
        })),
      }));
    }
  }

  const filters = [
    {
      key: "date",
      label: label("date-singular"),
      type: "date",
    },
    {
      key: "statuses",
      label: label("status-plural"),
      options: options.statuses || [],
    },
    {
      key: "branchGroups",
      label: label("branch-group-plural") || "Branch groups",
      options: options.branchGroups || [],
    },
    {
      key: "branches",
      label: label("branch-plural"),
      options: options.branches || [],
    },
    {
      key: "audits",
      label: label("audit-plural"),
      options: options.audits || [],
    },
    {
      key: "users",
      label: label("user-plural"),
      options: options.users || [],
    },
  ];

  useEffect(() => {
    getOptions();
  }, []);

  function buildQuery() {
    const newQuery = {};

    filters.forEach((filter) => {
      const value = searchParams.get(filter.key);
      if (value) newQuery[filter.key] = value;
    });

    const from = searchParams.get("from");
    const to = searchParams.get("to");
    if (from && to) {
      newQuery.dateFrom = from;
      newQuery.dateTo = to;
    }

    return newQuery;
  }

  function applyFilters() {
    setSearchParams((curr) => {
      curr.set("page", "1");
      return curr;
    });
    setSearchSignal({ ...buildQuery() });
  }

  async function getNcrs(
    internalQuery,
    callback,
    pageRetried = false,
    withFilters = false,
  ) {
    const request = ++listRequest.current;
    if (internalQuery) setQuery(internalQuery);

    const requestQuery = internalQuery || query;
    if (withFilters) filtersLoaded.current = false;

    const response = await ncrService.getNcrs({
      ...requestQuery,
      includeFilters: withFilters || !filtersLoaded.current,
    });
    if (request !== listRequest.current) {
      // Settle the shared search's loading state with the latest accepted data.
      // Its callback never receives the older request's records.
      callback?.([{ ok: true }, latestListResult.current]);
      return response;
    }
    const [status, data] = response;

    if (!status.ok) {
      toast.error(data?.error?.message || data?.error || label("error"));
      callback?.(response);
      return response;
    }
    const lastSkip = Math.max(
      0,
      (Math.ceil(data.count / (requestQuery.take || 10)) - 1) *
        (requestQuery.take || 10),
    );
    if (!pageRetried && Number(requestQuery.skip || 0) > lastSkip) {
      setSearchParams((curr) => {
        curr.set("page", String(lastSkip / (requestQuery.take || 10) + 1));
        return curr;
      });
      return getNcrs(
        { ...requestQuery, skip: lastSkip },
        callback,
        true,
        withFilters,
      );
    }

    latestListResult.current = { data: data.data, count: data.count };
    setNcrs(data.data);
    setCount(data.count);

    if (data.filters) {
      filtersLoaded.current = true;
      setOptions((options) => ({
        ...options,
        users:
          data.filters?.users?.map((user) => ({
            id: user.id,
            label: user.name,
          })) || [],
        branchGroups:
          data.filters?.branchGroups?.map((group) => ({
            id: group.id,
            label: group.name,
          })) || [],
        branches:
          data.filters?.branches?.map((branch) => ({
            id: branch.id,
            label: branch.name,
            groupId: branch.branchGroup?.id,
            groupLabel: branch.branchGroup?.name,
          })) || [],
        audits:
          data.filters?.audits?.map((audit) => ({
            id: audit.id,
            label: audit.description || `${label("audit-singular")} #${audit.id}`,
          })) || [],
      }));
    }

    if (callback) await callback(response);
    return response;
  }

  useEffect(() => {
    getNcrs({
      ...buildQuery(),
      skip: (Math.max(1, Number(searchParams.get("page")) || 1) - 1) * 10,
      take: 10,
    }, undefined, undefined, true);
    return () => {
      listRequest.current++;
    };
  }, []);

  async function handleCreateNcr(newElement) {
    const [response, data] = await ncrService.createManyNcrs(newElement);
    if (response.ok) {
      generationDirty.current = true;
      return { success: true };
    }
    return {
      error:
        data?.error || label("unable-to-create-ncr") || "Unable to create NCR",
    };
  }

  async function handleUpdateNcr(newElement) {
    const [response, data] = await ncrService.updateNcr(newElement.id, {
      referenceClause: newElement.referenceClause,
      personInCharge: newElement.personInCharge,
      teamMember: newElement.teamMember,
      branchGroupDescription: newElement.branchGroupDescription,
      estimatedClosureDate: newElement.estimatedClosureDate,
      statusId: newElement.statusId,
      repetitionTime: newElement.repetitionTime,
      isRepeated: newElement.isRepeated,
      isReprinted: newElement.isReprinted,
      personInChargeSignature: newElement.personInChargeSignature,
      headOfQualitySignature: newElement.headOfQualitySignature,
    });

    if (response.ok) {
      getNcrs();
      return { success: true };
    }
    return {
      error: data?.error || label("unable-to-save-ncr") || "Unable to save NCR",
    };
  }

  async function handleUpdateDetail(ncrId, detail) {
    const [response, data] = await ncrService.updateNcrDetail(
      ncrId,
      detail.id,
      {
        rootCause: detail.rootCause,
        correctiveAction: detail.correctiveAction,
        remarks: detail.remarks,
        statusId: detail.statusId,
      },
    );

    if (response.ok) {
      getNcrs();
      return { success: true };
    }
    return {
      error:
        data?.error ||
        label("unable-to-save-ncr-detail") ||
        "Unable to save NCR detail",
    };
  }

  async function handleDeleteNcr(ncr) {
    setNcrToDelete(ncr);
    setDeleteAlertOpen(true);
  }

  async function confirmDeleteNcr() {
    if (!ncrToDelete) return;
    const [response, data] = await ncrService.removeNcr(ncrToDelete.id);
    if (!response.ok) {
      toast.error(data?.error?.message || data?.error || label("error"));
      return;
    }
    toast.success(label("ncr-deleted") || "NCR deleted");
    getNcrs();
  }
  async function handleGeneratePdf(ncr, lang = "en") {
    const toastId = toast.loading(label("loading") || "Generating PDF");
    try {
      const [response, data] = await ncrService.generateNcrReportLink(ncr.id, {
        lang,
      });
      if (!response.ok || !data?.url)
        throw new Error(
          data?.error?.message ||
            data?.error ||
            label("unable-to-generate-pdf") ||
            "Unable to generate PDF",
        );
      const pdfResponse = await fetch(data.url);
      if (pdfResponse.ok === false)
        throw new Error(`PDF download failed (${pdfResponse.status})`);
      const blob = await pdfResponse.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const reportIdentifier = String(
        ncr.code ||
          ncr.audit?.description ||
          ncr.audit?.name ||
          ncr.id,
      )
        .trim()
        .replace(/\s*[<>:"/\\|?*\u0000-\u001F]+\s*/g, "-")
        .replace(/\s+/g, " ")
        .replace(/[. ]+$/g, "");
      link.href = url;
      link.download = `NCR-${reportIdentifier || ncr.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success(label("saved") || "PDF generated");
      return { success: true };
    } catch (failure) {
      toast.error(
        failure.message ||
          label("unable-to-generate-pdf") ||
          "Unable to generate PDF",
      );
      return { error: failure.message };
    } finally {
      toast.dismiss(toastId);
    }
  }

  async function handleGenerateMultiplePdf() {
    if (!selectedNcrIds.length || printingMultiple) return;
    setPrintingMultiple(true);
    const toastId = toast.loading(label("loading") || "Generating PDF");
    try {
      const [response, data] = await ncrService.generateMultipleNcrReportLink(
        selectedNcrIds,
        { lang: languageId },
      );
      if (!response.ok || !data?.url)
        throw new Error(data?.error?.message || data?.error || label("unable-to-generate-pdf") || "Unable to generate PDF");
      const pdfResponse = await fetch(data.url);
      if (pdfResponse.ok === false)
        throw new Error(`PDF download failed (${pdfResponse.status})`);
      const url = URL.createObjectURL(await pdfResponse.blob());
      const link = document.createElement("a");
      link.href = url;
      link.download = `NCR-reports-${selectedNcrIds.length}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success(label("saved") || "PDF generated");
    } catch (failure) {
      toast.error(failure.message || label("unable-to-generate-pdf") || "Unable to generate PDF");
    } finally {
      toast.dismiss(toastId);
      setPrintingMultiple(false);
    }
  }

  const rows = useMemo(
    () =>
      ncrs?.length > 0
        ? ncrs.map((ncr) => ({
            data: ncr,
            printSelection: selectingNcrsToPrint ? (
              <Checkbox
                size="small"
                aria-label={`${label("select-ncr") || "Select NCR"} ${ncr.code || ncr.id}`}
                checked={selectedNcrIds.includes(ncr.id)}
                disabled={!selectedNcrIds.includes(ncr.id) && selectedNcrIds.length >= 50}
                onClick={(event) => event.stopPropagation()}
                onChange={(event) => setSelectedNcrIds((current) => event.target.checked
                  ? [...current, ncr.id]
                  : current.filter((id) => id !== ncr.id))}
              />
            ) : null,
            code: (
              <MDTypography
                variant="inherit"
                fontWeight="bold"
                color={ncr.code ? null : "GrayText"}
              >
                {ncr.code || "—"}
              </MDTypography>
            ),
            branchGroup: (
              <MDTypography
                variant="inherit"
                color={ncr.branchGroup?.name ? null : "GrayText"}
              >
                {ncr.branchGroup?.name ? (
                  <>
                    {ncr.branchGroup.name}
                    <br />
                    {ncr.branch?.name}
                  </>
                ) : (
                  "—"
                )}
              </MDTypography>
            ),
            status: (
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {Array.from(
                  new Map(
                    (ncr.details || []).map((detail) => [
                      detail.statusId,
                      detail,
                    ]),
                  ).values(),
                ).map((detail) => (
                  <Chip
                    key={detail.statusId}
                    label={detail.status?.name || "—"}
                    sx={{ color: "white !important" }}
                    size="small"
                    color={NCR_STATUS_COLORS[detail.statusId] || "default"}
                  />
                ))}
                {!ncr.details?.length && (
                  <MDTypography variant="inherit" color="text">
                    —
                  </MDTypography>
                )}
              </Box>
            ),
            referenceClause: (
              <MDTypography
                variant="inherit"
                color={ncr.referenceClause ? null : "GrayText"}
              >
                {ncr.referenceClause || "—"}
              </MDTypography>
            ),
            personInCharge: (
              <MDTypography
                variant="inherit"
                color={ncr.personInCharge ? null : "GrayText"}
              >
                {ncr.personInCharge || "—"}
              </MDTypography>
            ),
            estimatedClosureDate: (
              <MDTypography variant="inherit">
                {ncr.estimatedClosureDate
                  ? moment(new Date(ncr.estimatedClosureDate)).format(
                      "DD/MM/YYYY",
                    )
                  : "—"}
              </MDTypography>
            ),
            creationDate: (
              <MDTypography variant="inherit">
                {moment(new Date(ncr.creationDate)).format("DD/MM/YYYY")}
              </MDTypography>
            ),
            actions: (
              <MDBox
                display="flex"
                flexDirection="row"
                justifyContent="end"
                alignItems="center"
              >
                {permissions.create && (
                  <Tooltip
                    title={label("create-from-ncr") || "Create from this NCR"}
                  >
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        open.change("generate", {
                          open: Math.random(),
                          sourceNcr: ncr,
                        });
                      }}
                    >
                      <Icon>content_copy</Icon>
                    </IconButton>
                  </Tooltip>
                )}
                {permissions.update && (
                  <Tooltip title={label("edit-ncr") || "Edit NCR"}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        open.change("edit", {
                          open: Math.random(),
                          data: ncr,
                        });
                      }}
                    >
                      <Icon>edit</Icon>
                    </IconButton>
                  </Tooltip>
                )}
                {(permissions.get || permissions.getMy) && (
                  <Tooltip title={label("generate-pdf") || "Generate PDF"}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGeneratePdf(ncr, languageId);
                      }}
                    >
                      <Icon>picture_as_pdf</Icon>
                    </IconButton>
                  </Tooltip>
                )}
                {permissions.delete && (
                  <Tooltip title={label("delete-ncr") || "Delete NCR"}>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteNcr(ncr);
                      }}
                    >
                      <Icon>delete</Icon>
                    </IconButton>
                  </Tooltip>
                )}
              </MDBox>
            ),
          }))
        : [],
    [
      ncrs,
      permissions.create,
      permissions.get,
      permissions.getMy,
      permissions.delete,
      permissions.update,
      label,
      languageId,
      selectingNcrsToPrint,
      selectedNcrIds,
    ],
  );

  const hasViewPermission = Boolean(
    permissions.get ||
      permissions.getMy ||
      permissions.create ||
      permissions.update,
  );

  searchHandler.current = async (request, callback) => {
    setSearchParams((curr) => {
      curr.set("page", "1");
      return curr;
    });
    await getNcrs(
      {
        autocomplete: request.autocomplete || "",
        ...buildQuery(),
        skip: 0,
        take: 10,
      },
      callback,
    );
  };
  return (
    <>
      <AlertDialog
        open={deleteAlertOpen}
        setOpen={setDeleteAlertOpen}
        title={label("delete-alert-title")}
        content={label("delete-alert-message")}
        onOk={confirmDeleteNcr}
      />
      <GenerateNcrDialog
        isOpen={open.get("generate")?.open}
        sourceNcr={open.get("generate")?.sourceNcr}
        onSubmit={handleCreateNcr}
        onClose={() => {
          open.change("generate", false);
          if (generationDirty.current) {
            generationDirty.current = false;
            getNcrs(undefined, undefined, false, true);
          }
        }}
      />
      <NcrEditDialog
        isOpen={open.get("edit")?.open}
        onSubmit={handleUpdateNcr}
        onSubmitDetail={handleUpdateDetail}
        statuses={options.statuses || []}
        onClose={() => open.change("edit", false)}
        data={open.get("edit")?.data}
      />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                gap={1}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  {label("ncr-plural") || "NCRs"}{" "}
                  {hasViewPermission ? `(${count || 0})` : ""}
                </MDTypography>
                <Box sx={{ flex: 1 }} />
                {permissions.create && (
                  <MDButton
                    onClick={() => {
                      open.change("generate", { open: Math.random() });
                    }}
                  >
                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                    &nbsp;{label("generate-ncr-button") || "Generate NCR"}
                  </MDButton>
                )}
                {(permissions.get || permissions.getMy) && (
                  selectingNcrsToPrint ? (
                    <>
                      <MDButton
                        variant="outlined"
                        color="white"
                        disabled={!selectedNcrIds.length || printingMultiple}
                        onClick={handleGenerateMultiplePdf}
                      >
                        <Icon>picture_as_pdf</Icon>&nbsp;
                        {printingMultiple
                          ? label("loading") || "Generating PDF"
                          : `${label("download-selected-ncr-reports") || "Download selected reports"} (${selectedNcrIds.length}/50)`}
                      </MDButton>
                      <MDButton
                        variant="outlined"
                        color="white"
                        onClick={() => {
                          setSelectingNcrsToPrint(false);
                          setSelectedNcrIds([]);
                        }}
                      >
                        {label("cancel-button") || "Cancel"}
                      </MDButton>
                    </>
                  ) : (
                    <MDButton onClick={() => setSelectingNcrsToPrint(true)}>
                      <Icon>print</Icon>&nbsp;{label("print-multiple-ncrs") || "Print multiple"}
                    </MDButton>
                  )
                )}
              </MDBox>

              {hasViewPermission ? (
                <>
                  <MDBox
                    p={2}
                    mt={2}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      width: "100%",
                      gap: 1,
                    }}
                  >
                    <FiltersDialog
                      onChange={applyFilters}
                      filters={filters}
                      open={open.get("filters")}
                      handleClose={() => open.change("filters", false)}
                    />
                    <Badge
                      badgeContent={Object.keys(buildQuery()).length}
                      color="primary"
                    >
                      <IconButton onClick={() => open.change("filters", true)}>
                        <FilterList />
                      </IconButton>
                    </Badge>
                    <Box sx={{ flex: 1 }}>
                      <TableSearch
                        id="all-ncr"
                        internalLoading={!Boolean(ncrs)}
                        fullWidth
                        setData={setNcrs}
                        setCount={setCount}
                        refreshSignal={searchSignal}
                        refreshSignalToQuery={(v) => v}
                        onSearch={(request, callback) =>
                          searchHandler.current(request, callback)
                        }
                        label={
                          label("search-ncr-label") || "Search by NCR code"
                        }
                      />
                    </Box>
                  </MDBox>

                  <MDBox>
                    <DataTable
                      loading={!Boolean(ncrs)}
                      table={{ columns, rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                    <MDBox sx={{ padding: 2 }}>
                      <Pagination
                        count={Math.max(1, Math.ceil(count / 10))}
                        color={"info"}
                        variant="outlined"
                        page={Number(searchParams.get("page")) || 1}
                        onChange={(_, newPage) => {
                          setSearchParams((curr) => {
                            curr.set("page", newPage);
                            return curr;
                          });

                          getNcrs({
                            ...query,
                            skip: (newPage - 1) * 10,
                          });
                        }}
                      />
                    </MDBox>
                  </MDBox>
                </>
              ) : (
                <AccessDenied type="component" />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </>
  );
}
